import { useState } from 'react';
import type { LeadDetail } from '../api/crm';
import { clientFlow } from './crm-flow';
import './crm-affiliate-flow.css';
import { promptDialog } from '../shell/dialogs';

function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

export function CrmClientFlow({
  lead,
  client,
  busy,
  onIntroCall,
  onOfferSent,
  onContractSent,
  onConvert,
  onOpenClient,
  onReject,
  onReopen,
  requireProduct = false,
  offerUrlFallback = null,
}: {
  lead: LeadDetail;
  client: { partnerId: number; name: string } | null;
  busy: boolean;
  onIntroCall: () => void;
  onOfferSent: (url: string | null) => void;
  onContractSent: () => void;
  onConvert: () => void;
  onOpenClient?: (name: string, partnerId: number) => void;
  onReject: () => void;
  onReopen: () => void;
  requireProduct?: boolean;
  offerUrlFallback?: string | null;
}) {
  const intro = !!lead.introCallAt;
  const offer = !!lead.offerSentAt;
  const contract = !!lead.contractSentAt;
  const dealOnly = (lead.services ?? []).length > 0 && (lead.services ?? []).every((s) => s === 'pozvanka');
  const converted = !!lead.clientPartnerId;
  const rejected = lead.status === 'rejected';
  const hasProduct = !requireProduct || (lead.services ?? []).length > 0;
  const productNote = hasProduct ? null : <span className="crm-aflow-note">Lead nemá vybraný produkt. Vyberte ho nahoře v poli Produkt.</span>;
  const [url, setUrl] = useState(lead.offerUrl ?? '');

  const flow = clientFlow(lead);
  const subs = [
    `${fmtDate(lead.createdAt)} · ${lead.confirmedAt ? 'e-mail ověřen' : 'e-mail neověřen'}`,
    intro ? `Proběhl ${fmtDate(lead.introCallAt)}` : 'Představení, co děláme',
    offer ? `Odesláno ${fmtDate(lead.offerSentAt)}` : 'Webová prezentace',
    contract || converted ? 'Dohodnuto' : offer ? 'Probíhá' : 'Meetingy a cena',
    converted ? `Smlouva podepsána ${fmtDate(lead.convertedAt)}` : contract ? `Odesláno k podpisu ${fmtDate(lead.contractSentAt)}` : dealOnly ? 'Podpis smlouvy, deal Pozvánka' : 'Podpis smlouvy, karta klienta',
  ];
  const steps = flow.labels.map((label, i) => ({ label, sub: subs[i] ?? '', done: !!flow.done[i] }));
  const current = rejected ? -1 : flow.current;
  const doneCount = flow.doneCount;
  const rejectLink = (
    <button type="button" className="btn btn-link crm-aflow-link is-danger" disabled={busy} onClick={onReject}>Neschválit</button>
  );

  const offerHref = lead.offerUrl ?? offerUrlFallback;
  const offerLink = offerHref
    ? <a className="btn btn-link crm-aflow-link" href={offerHref} target="_blank" rel="noreferrer">Otevřít nabídku</a>
    : null;

  let title: string;
  let text: string;
  let actions: JSX.Element | null;
  if (rejected) {
    title = 'Zájemce neschválen';
    text = 'Lead je uzavřený jako neschválený, například spam nebo poptávka, která nevyhovuje. Když jste se spletli, vraťte ho zpět do jednání.';
    actions = (
      <button type="button" className="btn btn-outline-secondary crm-aflow-btn is-outline" disabled={busy} onClick={onReopen}>Vrátit do jednání</button>
    );
  } else if (!intro) {
    title = 'Úvodní kontakt';
    text = 'Zavolejte klientovi a představte mu, co děláme. Po prvním callu krok označte.';
    actions = (
      <>
        <button type="button" className="btn btn-primary crm-aflow-btn" disabled={busy} onClick={onIntroCall}>Úvodní call proběhl</button>
        {productNote}
        {rejectLink}
      </>
    );
  } else if (!offer) {
    title = 'Připravte nabídku';
    text = 'Nabídku posíláme formou webové prezentace. Po odeslání ji tady označte a případně vložte odkaz na prezentaci.';
    actions = (
      <>
        <input className="form-control crm-aflow-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Odkaz na prezentaci (nepovinné)" aria-label="Odkaz na prezentaci" disabled={busy} />
        <button type="button" className="btn btn-primary crm-aflow-btn" disabled={busy} onClick={() => onOfferSent(url.trim() || null)}>Nabídka odeslána</button>
        {productNote}
        {rejectLink}
      </>
    );
  } else if (!contract && !converted) {
    title = 'Připomínky a jednání';
    text = 'Čekáme na připomínky klienta, další upřesňující meetingy nebo jednání o ceně. Když je deal dohodnutý, pošlete klientovi smlouvu k podpisu a krok označte.';
    actions = (
      <>
        <button type="button" className="btn btn-primary crm-aflow-btn" disabled={busy} onClick={onContractSent}>Smlouva odeslána k podpisu</button>
        {offerLink}
        <button
          type="button"
          className="btn btn-link crm-aflow-link"
          disabled={busy}
          onClick={() => {
            void promptDialog({ title: 'Odkaz na nabídku', label: 'Odkaz na prezentaci s nabídkou', initial: lead.offerUrl ?? '', placeholder: 'https://…' })
              .then((next) => { if (next !== null) onOfferSent(next.trim() || null); });
          }}
        >
          {lead.offerUrl ? 'Změnit odkaz' : 'Přidat odkaz na nabídku'}
        </button>
        {rejectLink}
      </>
    );
  } else if (!converted) {
    title = 'Čekáme na podpis smlouvy';
    text = dealOnly
      ? 'Smlouva je u klienta k podpisu. Po podpisu zapište Pozvánku jako deal. Lead se připojí ke kartě klienta a na ní vznikne deal Pozvánka ve stavu Připraveno.'
      : 'Smlouva je u klienta k podpisu. Po podpisu ho získejte jako klienta. Založí se karta v Klientech a lead se uzavře jako úspěšný.';
    actions = (
      <>
        <button type="button" className="btn btn-primary crm-aflow-btn" disabled={busy || !hasProduct} onClick={onConvert}>
          {dealOnly ? 'Smlouva podepsána, zapsat jako deal' : 'Smlouva podepsána, získat jako klienta'}
        </button>
        {productNote}
        {offerLink}
        {rejectLink}
      </>
    );
  } else {
    title = dealOnly ? 'Hotovo, deal je zapsaný' : 'Hotovo';
    text = dealOnly
      ? `Smlouva je podepsaná a Pozvánka je zapsaná jako deal na kartě klienta${client ? ` ${client.name}` : ''}. Deal aktivujte na kartě klienta, až se Pozvánka zapne.`
      : client ? `Smlouva je podepsaná a klient ${client.name} je založený v Klientech.` : 'Smlouva je podepsaná a klient je založený v Klientech.';
    actions = (
      <>
        {client && onOpenClient ? <button type="button" className="btn btn-primary crm-aflow-btn" onClick={() => onOpenClient(client.name, client.partnerId)}>Otevřít kartu klienta</button> : null}
        {offerLink}
      </>
    );
  }

  return (
    <section className={`crm-aflow${rejected ? ' is-rejected' : ''}`} aria-label="Postup klienta">
      <header className="crm-aflow-head">
        <span className="crm-detail-eyebrow">Postup klienta</span>
        <span className="crm-aflow-count">{rejected ? 'Neschváleno' : `${doneCount} z ${steps.length}`}</span>
      </header>
      <ol className="crm-aflow-steps">
        {steps.map((s, i) => (
          <li key={s.label} className={`crm-aflow-step ${s.done ? 'is-done' : i === current ? 'is-current' : ''}`}>
            <span className="crm-aflow-dot" aria-hidden="true">{s.done ? '✓' : i + 1}</span>
            <span className="crm-aflow-label">{s.label}</span>
            <span className="crm-aflow-sub">{s.sub}</span>
          </li>
        ))}
      </ol>
      <div className="crm-aflow-action">
        <div className="crm-aflow-copy">
          <strong>{title}</strong>
          <p>{text}</p>
        </div>
        {actions && <div className="crm-aflow-buttons">{actions}</div>}
      </div>
    </section>
  );
}
